/**
 * <s-description-tabs> — pill tabs for the product description sections
 * Liquid renders one pill (role="tab") and one panel (role="tabpanel") per
 * description heading. Without JS every panel stays visible; once connected,
 * only the selected panel shows.
 *
 * Commands (on pills, commandfor this element):
 *   --show — select the pill that sent the command
 */
class SDescriptionTabs extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const tabs = this.#tabs();
    if (!tabs.length) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--show' && event.source) this.#select(event.source);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      const current = event.target.closest('[role="tab"]');
      if (!current) return;
      const list = this.#tabs();
      const i = list.indexOf(current);
      const rtl = getComputedStyle(this).direction === 'rtl';
      let next = null;
      if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = list[(i + 1) % list.length];
      if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = list[(i - 1 + list.length) % list.length];
      if (event.key === 'Home') next = list[0];
      if (event.key === 'End') next = list[list.length - 1];
      if (!next) return;
      event.preventDefault();
      this.#select(next);
      next.focus();
    }, { signal });

    this.#select(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  #select(tab) {
    for (const t of this.#tabs()) {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = !selected;
    }
  }
}

if (!customElements.get('s-description-tabs')) customElements.define('s-description-tabs', SDescriptionTabs);
